import {
  CustomFormattedMessage,
  CustomFormattedMessageProps,
  TranslationsKeys,
  useCustomIntl,
} from '../../../../i18n/i18n';
import { useAppDispatch } from '../../../../state/hooks';
import {
  closeUserManualDialog,
  openUserManualDialog,
} from '../../../../state/dialogs/useUserManualDialog';
import { openSupportDialog } from '../../../../state/dialogs/useSupportDialog';
import { userManualDialogChapters } from '../../../../state/dialogs/chapters';
import { ChapterHeadline } from '../chapterComponents/ChapterHeadline';
import { CardManagementSection } from '../chapterComponents/CardManagementSection';
import { paymentMethodStateTextColors } from '../../../colors/paymentMethodStateTextColors';
import { PAYMENT_METHOD_STATE_TO_TRANSLATION_KEY } from '../../../../i18n/enumMappings';
import { imageBaseNames } from './imageAssets';

const ManualParagraph = ({ id, values }: CustomFormattedMessageProps) => (
  <p className="margin-0">
    <CustomFormattedMessage
      id={id}
      values={values}
    />
  </p>
);

// Inline text link styling, without the height a RIO `btn` adds to the line.
const inlineLinkClassName =
  'border-none bg-none padding-0 text-color-primary text-medium text-decoration-underline';

const useChapterLinks = () => {
  const dispatch = useAppDispatch();
  const chapterLink = (chapter: string) => {
    const renderLink = (content: React.ReactNode) => (
      <button
        type="button"
        className={inlineLinkClassName}
        onClick={() => dispatch(openUserManualDialog(chapter))}
      >
        {content}
      </button>
    );
    return renderLink;
  };
  return {
    overview: chapterLink(userManualDialogChapters.getStartedOverview),
    addingCards: chapterLink(userManualDialogChapters.addingServiceCards),
    activatingServices: chapterLink(
      userManualDialogChapters.activatingServices,
    ),
    services: (content: React.ReactNode) => <b>{content}</b>,
    support: (content: React.ReactNode) => (
      <button
        type="button"
        className={inlineLinkClassName}
        onClick={() => {
          dispatch(closeUserManualDialog());
          dispatch(openSupportDialog());
        }}
      >
        {content}
      </button>
    ),
  };
};

export const CardManagementVehicleOverviewChapter = () => (
  <div>
    <div className="row">
      <ChapterHeadline messageId="userManual.vehicleOverview.headline" />
    </div>
    <CardManagementSection
      image={imageBaseNames.cardManagement_vehicleOverview_tableFilters}
      title="userManual.vehicleOverview.headline"
    >
      <ManualParagraph id="userManual.cardManagement.vehicleOverview.intro" />
      <ManualParagraph id="userManual.cardManagement.vehicleOverview.filters" />
    </CardManagementSection>
    <CardManagementSection
      image={imageBaseNames.cardManagement_vehicleOverview_services}
      title="userManual.cardManagement.images.activeServices"
    >
      <ManualParagraph id="userManual.cardManagement.vehicleOverview.services" />
    </CardManagementSection>
    <CardManagementSection
      image={
        imageBaseNames.cardManagement_vehicleOverview_vehicle_configuration
      }
      title="userManual.cardManagement.images.vehicleConfiguration"
    >
      <ManualParagraph id="userManual.cardManagement.vehicleOverview.configuration" />
    </CardManagementSection>
  </div>
);

const statuses = [
  {
    state: 'ACTIVE',
    description: 'userManual.addingServiceCards.status.active.description',
  },
  {
    state: 'ONBOARDING_PENDING',
    description: 'userManual.addingServiceCards.status.pending.description',
  },
  {
    state: 'ONBOARDING_FAILURE',
    description: 'userManual.cardManagement.addingCards.failed',
  },
] as const;

export const CardManagementAddingCardsChapter = () => {
  const links = useChapterLinks();
  return (
    <div>
      <div className="row">
        <ChapterHeadline messageId="userManual.cardManagement.addingCards.headline" />
      </div>
      <CardManagementSection
        image={imageBaseNames.cardManagement_addingCards_vehicle_configuration}
        title="userManual.cardManagement.images.vehicleConfiguration"
      >
        <ManualParagraph
          id="userManual.cardManagement.addingCards.intro"
          values={links}
        />
      </CardManagementSection>
      <CardManagementSection
        image={imageBaseNames.cardManagement_addingCards_newCard}
        title="userManual.cardManagement.images.addingCards"
      >
        <ManualParagraph id="userManual.cardManagement.addingCards.details" />
        <ManualParagraph id="userManual.cardManagement.addingCards.authentication" />
      </CardManagementSection>
      <CardManagementSection
        image={imageBaseNames.cardManagement_addingCards_editServices}
        title="userManual.cardManagement.images.cardStatuses"
        separator
      >
        <ManualParagraph id="userManual.addingServiceCards.status.text" />
        {statuses.map(({ state, description }) => (
          <p
            key={state}
            className="margin-0"
          >
            <span
              className={`display-block ${paymentMethodStateTextColors[state]}`}
            >
              <CustomFormattedMessage
                id={PAYMENT_METHOD_STATE_TO_TRANSLATION_KEY[state]}
              />
            </span>
            <CustomFormattedMessage
              id={description}
              values={links}
            />
          </p>
        ))}
      </CardManagementSection>
      <CardManagementSection
        image={imageBaseNames.cardManagement_addingCards_failedCard}
        title="userManual.cardManagement.images.failedActivation"
        separator
        wideImage
      >
        <ManualParagraph id="userManual.addingServiceCards.status.activationFailed.text" />
      </CardManagementSection>
      <CardManagementSection
        image={imageBaseNames.cardManagement_addingCards_deletionConfirmation}
        title="userManual.cardManagement.images.removingCards"
        separator
      >
        <ManualParagraph id="userManual.cardManagement.addingCards.remove" />
        <ManualParagraph
          id="userManual.cardManagement.addingCards.next"
          values={links}
        />
      </CardManagementSection>
    </div>
  );
};

const capabilities: { label: TranslationsKeys; providers: boolean[] }[] = [
  {
    label: 'userManual.cardManagement.capabilities.registration',
    providers: [true, true, true],
  },
  {
    label: 'userManual.cardManagement.capabilities.reports',
    providers: [true, true, true],
  },
  {
    label: 'userManual.cardManagement.capabilities.export',
    providers: [true, true, true],
  },
  {
    label: 'userManual.cardManagement.capabilities.fuelling',
    providers: [true, true, false],
  },
  {
    label: 'userManual.cardManagement.capabilities.parking',
    providers: [true, false, false],
  },
  {
    label: 'userManual.cardManagement.capabilities.other',
    providers: [true, false, false],
  },
  {
    label: 'userManual.cardManagement.capabilities.breakdowns',
    providers: [true, false, false],
  },
];

export const CardManagementActivatingServicesChapter = () => {
  const links = useChapterLinks();
  const { formatMessage } = useCustomIntl();
  return (
    <div>
      <div className="row">
        <ChapterHeadline messageId="userManual.activatingServices.headline" />
      </div>
      <CardManagementSection
        image={
          imageBaseNames.cardManagement_activatingServices_vehicleConfiguration
        }
        title="userManual.cardManagement.images.vehicleConfiguration"
      >
        <ManualParagraph id="userManual.cardManagement.activatingServices.intro" />
        <ManualParagraph id="userManual.cardManagement.activatingServices.defaults" />
      </CardManagementSection>
      <CardManagementSection
        image={imageBaseNames.cardManagement_activatingServices_services}
        title="userManual.activatingServices.headline"
        separator
      >
        <ManualParagraph
          id="userManual.cardManagement.activatingServices.configure"
          values={links}
        />
        <ManualParagraph
          id="userManual.cardManagement.activatingServices.services"
          values={links}
        />
        <ManualParagraph id="userManual.cardManagement.activatingServices.charging" />
        <ManualParagraph id="userManual.cardManagement.activatingServices.providers" />
        <div className="table-responsive">
          <table
            className="table table-striped text-size-12"
            aria-label={formatMessage({ id: 'common.services' })}
          >
            <thead>
              <tr>
                <th
                  scope="col"
                  aria-label={formatMessage({ id: 'common.services' })}
                />
                <th scope="col">UTA Edenred</th>
                <th scope="col">DKV</th>
                <th scope="col">MAN Charge&amp;Go</th>
              </tr>
            </thead>
            <tbody>
              {capabilities.map(({ label, providers }) => (
                <tr key={label}>
                  <th
                    scope="row"
                    className="text-normal"
                  >
                    <CustomFormattedMessage id={label} />
                  </th>
                  {providers.map((available, index) => (
                    <td
                      key={index}
                      className="text-center"
                    >
                      {available ? 'X' : ''}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardManagementSection>
      <CardManagementSection
        image={imageBaseNames.cardManagement_activatingServices_removeService}
        title="userManual.cardManagement.images.deactivatingServices"
        separator
      >
        <ManualParagraph id="userManual.cardManagement.activatingServices.deactivate" />
      </CardManagementSection>
    </div>
  );
};
